import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/** Narrow shapes for Web Speech; full DOM typings vary by TS/browser. */
interface SpeechRecognitionResultLike {
  readonly isFinal: boolean;
  readonly 0: { readonly transcript: string };
}

interface SpeechRecognitionResultListLike {
  readonly length: number;
  [index: number]: SpeechRecognitionResultLike;
}

interface SpeechRecognitionEventLike {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultListLike;
}

interface SpeechRecognitionErrorEventLike {
  readonly error: string;
  readonly message?: string;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((ev: SpeechRecognitionEventLike) => void) | null;
  onerror: ((ev: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

const getSpeechRecognitionCtor = (): SpeechRecognitionCtor | null => {
  if (typeof globalThis === "undefined") return null;
  const g = globalThis as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return g.SpeechRecognition ?? g.webkitSpeechRecognition ?? null;
};

/** Normalized Web Speech `onerror` payload. */
export interface VoiceRecognitionErrorInfo {
  code: string;
  message?: string;
}

/** Toast copy for `onError`, or `null` to skip. */
export const voiceRecognitionToastMessage = (
  info: VoiceRecognitionErrorInfo,
): string | null => {
  if (info.code === "aborted") return null;
  switch (info.code) {
    case "not-allowed":
    case "service-not-allowed":
      return "Microphone access was denied. Allow the microphone to use voice input.";
    case "network":
      return "Voice input failed due to a network error. Try again.";
    case "audio-capture":
      return "No microphone was found or it could not be opened.";
    case "no-speech":
      return null;
    default:
      return info.message?.trim() || `Voice input error (${info.code}).`;
  }
};

export interface UseVoiceToTextOptions {
  onFinalPhrase: (phrase: string) => void;
  /** When true, stops an active listening session (e.g. chat began streaming). */
  shouldAbort?: boolean;
  onError?: (info: VoiceRecognitionErrorInfo) => void;
}

export interface UseVoiceToTextReturn {
  isRecording: boolean;
  isSupported: boolean;
  startListening: () => void;
  stopListening: () => void;
}

/** Web Speech dictation; requires secure context (HTTPS) where the browser enforces it. */
const useVoiceToText = ({
  onFinalPhrase,
  shouldAbort = false,
  onError,
}: UseVoiceToTextOptions): UseVoiceToTextReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const onFinalPhraseRef = useRef(onFinalPhrase);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onFinalPhraseRef.current = onFinalPhrase;
  }, [onFinalPhrase]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const stopListening = useCallback(() => {
    const rec = recognitionRef.current;
    // Clear ref before stop/abort so a late `onend` does not treat a stale instance as active.
    recognitionRef.current = null;
    if (rec) {
      try {
        rec.stop();
      } catch {
        try {
          rec.abort();
        } catch {
          /* ignore */
        }
      }
    }
    setIsRecording(false);
  }, []);

  useEffect(() => {
    if (shouldAbort) stopListening();
  }, [shouldAbort, stopListening]);

  useEffect(
    () => () => {
      stopListening();
    },
    [stopListening],
  );

  const startListening = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor || recognitionRef.current) return;

    const rec = new Ctor();
    recognitionRef.current = rec;
    rec.lang = typeof navigator !== "undefined" ? navigator.language : "en-US";
    rec.continuous = true;
    rec.interimResults = false;

    rec.onresult = (event: SpeechRecognitionEventLike) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result == null || !result.isFinal) continue;
        const phrase = result[0]?.transcript?.trim();
        if (phrase) onFinalPhraseRef.current(phrase);
      }
    };

    rec.onerror = (event: SpeechRecognitionErrorEventLike) => {
      onErrorRef.current?.({
        code: event.error,
        message: event.message,
      });
      stopListening();
    };

    rec.onend = () => {
      recognitionRef.current = null;
      setIsRecording(false);
    };

    try {
      rec.start();
      setIsRecording(true);
    } catch {
      recognitionRef.current = null;
      setIsRecording(false);
    }
  }, [stopListening]);

  const isSupported = useMemo(
    () =>
      typeof globalThis !== "undefined" && getSpeechRecognitionCtor() !== null,
    [],
  );

  return {
    isRecording,
    isSupported,
    startListening,
    stopListening,
  };
};

export default useVoiceToText;
